<template>
  <div id="mini-player" class="row" align-v="center">
    <div class="title" v-if="video">
      <router-link :to="videoRoute"><img class="cover" :src="getThumbnail(video)"></router-link>
      <div class="details">
        <router-link class="song-title" :to="videoRoute">
          {{ video.title }}
          <font-awesome-icon icon="check" v-if="inMyVideos" />
        </router-link>
        <router-link class="song-artist" :to="{name: 'artist-about', params: { artistId: video.artist.id }}">
          {{ video.artist.name }}
        </router-link>
      </div>
    </div>

    <div class="title" v-if="live">
      <img class="cover" :src="live.promo_image" v-if="live.promo_image" />
      <div class="details">
        <router-link class="song-title" :to="videoRoute">
          {{ live.title }}
        </router-link>
        <router-link class="song-artist" :to="{name: 'artist-about', params: { artistId: live.artist.id }}">
          {{ live.artist.name }}
        </router-link>
      </div>
    </div>

    <div class="playback col-auto" v-if="video">
      <div class="control">
        <font-awesome-icon v-if="videos && videos.length" icon="backward" class="ctl" @click="prev" />
        <font-awesome-icon :icon="playPauseIcon" class="ctl" @click="togglePlay" />
        <font-awesome-icon v-if="videos && videos.length" icon="forward" class="ctl" @click="next" />
      </div>
      <div class="bar-bg" @click="seek">
        <div class="bar-fg" ref="progressBar"></div>
      </div>
    </div>
    <div class="time" v-if="video">
      {{ currentMinSec }}
    </div>
    <div v-if="live">
      <div class="live online" v-if="live.is_live && this.isOnline">
          <font-awesome-icon icon="circle" /> &nbsp; LIVE
      </div>
      <div class="live ready" v-if="live.is_live && !this.isOnline">
          <font-awesome-icon icon="spinner" spin/>
          WAITING FOR STREAM
      </div>
      <div class="live stopped" v-if="!live.is_live">
          STOPPED
      </div>
    </div>

    <div class="volume">
      <font-awesome-icon icon="list" class="ctl" id="miniPlayerAddToPlaylist" v-if="video && isLoggedIn" />
      <AddToPlaylist target="miniPlayerAddToPlaylist" :video="video" v-if="video && isLoggedIn" />

      <font-awesome-icon :icon="volumeIcon" class="ctl" fixed-width @click="toggleMute" />
      <div class="bar-bg" @click="setVolume">
        <div class="bar-fg" v-bind:style="volumeBarStyle"></div>
      </div>
    </div>
    <font-awesome-icon icon="times" class="ctl" @click="stopPlayback" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/custom.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

#mini-player {
  display: none;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 10px;
  align-items: center;
  z-index: $zindex-miniplayer;

  opacity: 0.81;
  -webkit-backdrop-filter: blur(3.2px);
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.75), #000000);

  .bar-bg {
    width: 100%;
    height: 7px;
    border-radius: 1px;
    background-color: $blue-gray;
    margin-bottom: 6px;
    overflow: hidden;
  }

  .bar-fg {
    position: relative;
    transition: width 0.1s;
    height: 100%;
    border-radius: 1px;
    background-color: #cb3590;
  }

  .ctl {
    margin-left: 0.5em;
    margin-right: 0.5em;
    color: $blue-gray;
  }

  @media (hover: hover) {
    .ctl:hover {
      color: $white;
    }
  }

  .title {
    margin: auto;
    display: flex;
    width: 100%;
    text-align: center;
    justify-content: center;

    .cover {
      display: none;
    }

    .details {
      text-overflow: ellipsis;
      white-space: nowrap;

      .song-title {
        display: block;
        color: white;
        font-weight: bold;
      }

      .song-artist {
        display: block;
        color: white;
        font-size: 0.8em;
      }
    }
  }

  @include media-breakpoint-up(sm) {
    .title {
      width: 200px;
      text-align: left;
      justify-content: start;

      .cover {
        display: block;
        width: 40px;
        height: 40px;
        margin-right: 10px;
      }
    }
  }

  .live {
    &.online {
      background: $tomato;
      color: $white;
    }

    &.ready {
      background: $pale-gold;
      color: $dark;
    }

    &.stopped {
      background: $warm-grey;
      color: $white;
    }

    font-weight: bold;
    font-size: 0.8em;
    padding: 0.5em 1em 0.3em 1em;
    border-radius: 4px;
  }

  .playback {
    flex: 1;
    padding-left: 2em;
    padding-right: 0.3em;
    margin: auto 0 0 0;
    .control {
      text-align: center;
    }
  }

  .time {
    font-size: 0.8em;
    margin: auto 0 0 0;
    padding-right: 2em;
    min-width: 4em;
  }

  .volume {
    display: none;
    justify-content: flex-end;
    margin: auto;

    .bar-bg {
      display: inline-block;
      width: 100px;
      margin: auto 10px auto 10px;

      .bar-fg {
        transition: none;
      }
    }

    .ctl {
      margin-left: 5px;
      margin-right: 5px;
    }
  }

  @include media-breakpoint-up(md) {
    .volume {
      display: flex;
    }
  }
}
</style>

<script>
import { mapGetters } from 'vuex'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import QueryString from 'query-string'

import AddToPlaylist from './AddToPlaylist'
import { songHelpers } from '@/store/helpers'

import { getNewSrc } from '@/utils/dacast'

function getPercentage (ev) {
  var rect = ev.currentTarget.getBoundingClientRect()
  var pct = (ev.pageX - rect.left) / rect.width
  if (pct < 0) pct = 0
  if (pct > 1) pct = 1
  return pct
}

export default {
  name: 'MiniPlayer',
  components: {
    FontAwesomeIcon,
    AddToPlaylist
  },
  props: [
  ],
  data () {
    return {
      duration: null,
      currentTime: null,
      isPlaying: false,
      isOnline: false,
      volume: 0,
      video: null,
      live: null,
      videos: [],
      videoRoute: null,
      currentPosition: 0,
      currentVideoRecorded: false
    }
  },
  computed: {
    currentMinSec () {
      if (!this.currentTime) return '0:00'
      if (typeof this.currentTime === 'object') return '0:00'

      var txt = Math.floor(this.currentTime / 60) + ':'
      var sec = Math.floor(this.currentTime % 60)
      if (sec >= 10) {
        txt = txt + sec
      } else {
        txt = txt + '0' + sec
      }

      return txt
    },
    playPauseIcon () {
      return (this.isPlaying) ? 'pause' : 'play'
    },
    volumeIcon () {
      if (this.volume === 0) return 'volume-off'
      if (this.volume < 0.4) return 'volume-down'
      return 'volume-up'
    },
    volumeBarStyle () {
      return {
        width: Math.round(100 * this.volume) + '%'
      }
    },
    contentTitle () {
      if (this.live) return this.live.title
      return this.video ? this.video.title : null
    },
    artistName () {
      if (this.live && this.live.artist) return this.live.artist.name
      return (this.video && this.video.artist) ? this.video.artist.name : null
    },
    myVideos () {
      if (!this.isLoggedIn) return null
      return this.currentUser.default_playlists.my_videos
    },
    inMyVideos () {
      if (!this.video || !this.myVide) return false
      return this.myVideos.video_ids.indexOf(this.video.id) !== -1
    },
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('profile', ['currentUser']),
    ...mapGetters('realtime', ['notifications'])
  },
  methods: {
    receivePlayer: function ({ player, videoRoute, video, videos, position, live }) {
      const playerId = player.getContentInfo().contentId
      console.log('[mini player] received player with content id=' + playerId + ' from ' + videoRoute)
      this.dacastPlayer = window.dacastPlayer = player
      this.videoRoute = videoRoute
      this.video = video
      this.videos = videos
      this.currentPosition = position
      this.live = live
      this.isOnline = !!this.live

      this.wrapper.appendChild(player.getElement())
      this.$el.style.display = 'flex'

      this.duration = player.duration()
      this.currentTime = player.currentTime()
      this.progressCallbackId = window.setInterval(() => this.updateProgress(), 200)
      this.currentVideoRecorded = this.currentTime > 10

      if (this.live) {
        this.livePingIntervalId = window.setInterval(this.ping, 10000)
        this.ping()
      }

      player.on('complete', this.onComplete)

      window.setTimeout(() => {
        console.log('[mini player] resuming playback')
        player.play()
      }, 100)
    },
    releasePlayer: function (videoRoute) {
      let player = this.dacastPlayer
      if (!player || this.videoRoute !== videoRoute) return null

      console.log('[mini player] releasing player with video route=' + videoRoute)

      if (this.progressCallbackId) {
        window.clearInterval(this.progressCallbackId)
        this.progressCallbackId = null
      }

      if (this.livePingIntervalId) {
        window.clearInterval(this.livePingIntervalId)
        this.livePingIntervalId = null
      }

      this.dacastPlayer.off('complete', this.onComplete)

      let video = this.video
      let position = this.currentPosition
      let live = this.live

      this.dacastPlayer = null
      this.video = null
      this.live = null
      this.isPlaying = false
      this.$el.style.display = 'none'

      return {
        video: video,
        stream: live,
        player: player,
        position: position
      }
    },
    getCurrentVideo: function () {
      if (!this.dacastPlayer) return null
      return this.video
    },
    getCurrentLive: function () {
      if (!this.dacastPlayer) return null
      return this.live
    },
    stopPlayback: function () {
      if (!this.dacastPlayer) return

      console.log('[mini player] stopping playback')

      if (this.progressCallbackId) {
        window.clearInterval(this.progressCallbackId)
        this.progressCallbackId = null
      }

      if (this.livePingIntervalId) {
        window.clearInterval(this.livePingIntervalId)
        this.livePingIntervalId = null
      }

      this.dacastPlayer.dispose()
      this.dacastPlayer = null
      this.video = null
      this.live = null
      this.isPlaying = false
      this.$el.style.display = 'none'
    },
    getVolume: function () {
      if (!this.dacastPlayer) return undefined
      const instance = this.dacastPlayer.getPlayerInstance()
      if (!instance) return undefined

      return instance.volume
    },
    updateProgress: function () {
      var player = this.dacastPlayer
      if (!player) return

      this.duration = player.duration()
      this.currentTime = player.currentTime()
      this.isPlaying = player.playing()

      let volume = this.getVolume()
      if (volume !== null) this.volume = volume

      // Set directly instead via Vue prop to minimize work done on every frame
      if (this.isPlaying && !this.live) {
        this.$refs.progressBar.style.width = (100 * this.currentTime / this.duration) + '%'
      }

      if (this.live) {
        const instance = player.getPlayerInstance()
        if (instance) this.isOnline = instance.network.online
      } else {
        if (this.currentTime > 10) this.recordVideo()
      }
    },
    seek (ev) {
      var player = this.dacastPlayer
      if (!player) return

      var pct = getPercentage(ev)
      var time = pct * player.duration()

      console.log('[mini player] seek to ' + Math.round(100 * pct) + '% (' + Math.round(time) + 's)')
      player.currentTime(time)
    },
    setVolume (ev) {
      var player = this.dacastPlayer
      if (!player) return

      var pct = getPercentage(ev)
      if (pct < 0.1) pct = 0
      if (pct > 0.9) pct = 1

      console.log('[mini player] set volume to ' + Math.round(100 * pct) + '%')

      player.volume(pct)
      this.volume = pct
    },
    togglePlay () {
      var player = this.dacastPlayer
      if (!player) return

      if (player.playing()) {
        console.log('[mini player] pause')
        player.pause()
        this.isPlaying = false
      } else {
        console.log('[mini player] play')
        player.play()
        this.isPlaying = true
      }
    },
    toggleMute () {
      var player = this.dacastPlayer
      if (!player) return

      this.volume = (this.volume === 0) ? 1.0 : 0.0
      player.volume(this.volume)
    },
    next () {
      if (!this.videos || this.videos.length < 2) return
      this.switchVideo(this.currentPosition + 1)
    },
    onComplete () {
      this.next()
    },
    prev () {
      if (!this.videos || this.videos.length < 2) return
      this.switchVideo(this.currentPosition - 1)
    },
    async switchVideo (pos) {
      this.currentPosition = (pos + this.videos.length) % this.videos.length
      this.video = this.videos[this.currentPosition]
      this.currentTime = 0

      const src = await getNewSrc(this.dacastPlayer, this.video)
      this.dacastPlayer.stop()
      this.dacastPlayer.getPlayerInstance().src = src

      this.currentVideoRecorded = false
      if (this.video) {
        this.dacastPlayer.play()
        this.updateVideoRoute()
      }
    },
    backToVideo () {
      this.$router.push(this.videoRoute)
    },
    onNotification () {
      if (!this.isLoggedIn || !this.notifications.length) return

      for (let i = 0; i < this.notifications.length; i++) {
        let { type, content } = this.notifications[i]

        if ((type === 'live-block') && this.live && (this.live.id === content.id)) {
          this.stopPlayback()
          this.$store.dispatch('flash/showMessage', {
            type: 'error',
            message: 'You have been blocked from this Livestream'
          })
        }
      }
    },
    recordVideo () {
      if (!this.video || this.currentVideoRecorded) return
      this.currentVideoRecorded = true
      if (this.isLoggedIn) this.$store.dispatch('playlist/addToHistory', this.video.id)
      this.$store.dispatch('song/recordView', {id: this.video.id})
      this.video.view_count = this.video.view_count + 1
    },
    async ping () {
      if (!this.live) return

      const response = await this.$store.dispatch('live/pingLiveEvent', { id: this.live.id })
      if (!response) return

      if (response.has_ended) {
        this.$store.dispatch('flash/showMessage', {message: response.message || 'Live stream has ended'})
        this.stopPlayback()
      }
    },

    updateVideoRoute () {
      if (!this.videoRoute) return
      if (!this.video) return

      const url = new URL('http://example.com' + this.videoRoute)
      const qs = QueryString.parse(url.search)

      if (url.pathname.indexOf('/video/') === 0) {
        this.videoRoute = '/video/' + this.video.id + url.search
        console.log('[mini player] updating video route to', this.videoRoute)
        return
      }

      if ((url.pathname.indexOf('/playlist/') === 0) || (url.pathname.indexOf('/album/') === 0)) {
        qs.pos = this.currentPosition
        this.videoRoute = url.pathname + '?' + QueryString.stringify(qs)
        console.log('[mini player] updating video route to', this.videoRoute)
        return
      }

      console.log('[mini player] not updating unsupported route', this.videoRoute)
    },
    ...songHelpers
  },
  watch: {
    'isLoggedIn': 'stopPlayback',
    'notifications': 'onNotification'
  },
  created () {
    this.$eventHub.$on('stop-playback', this.stopPlayback)
  },
  mounted: function () {
    this.wrapper = document.getElementById('dacast-hidden-player-wrapper')
    this.dacastPlayer = null
    this.progressCallbackId = null
    this.livePingIntervalId = null
    this.$store.commit('setMiniPlayer', this)
  },
  beforeDestroy: function () {
    this.$eventHub.$off('stop-playback', this.stopPlayback)
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/MiniPlayer.vue